import { useEntitiesFromSearch } from 'api/search'
import { useMemo } from 'react'
import { PARENT_TYPES } from 'types'
import { useContactsItems } from '../contacts/useContactItems'
import { useUserItems } from '../users/useUserItems'

const toArray = (v: any) => (Array.isArray(v) ? v : v ? [v] : [])

const normalizeId = (v: any) => (typeof v === 'string' ? v.trim() : v)

const n = (v: any) => Number(v) || 0
const round2 = (v: number) => Math.round((v + 1e-12) * 100) / 100

const collectOrgContactIds = (org: any) =>
  Array.from(
    new Set(
      [
        ...toArray(org?.contacts),
        ...toArray(org?.additionalContacts),
        ...toArray(org?.billingContact),
        ...toArray(org?.primaryContact),
      ]
        .map(normalizeId)
        .filter(Boolean)
    )
  )
const collectOrgTeamIds = (org: any) => {
  const teamIds = org?.team_details?.map(u => u?.user_uuid) ?? []
  return Array.from(new Set([...toArray(teamIds), ...toArray(org?.owner)].map(normalizeId).filter(Boolean)))
}

export function useOrganizationView(PK?: string) {
  const { organizations, parents, deals, products, custom_products, isItemsLoading } = useEntitiesFromSearch()
  const { data: contacts, isLoading: isContactsLoading } = useContactsItems()
  const { data: users, isLoading: isUsersLoading } = useUserItems()

  return useMemo(() => {
    if (!PK || !organizations) {
      return {
        isItemsLoading: isItemsLoading || isContactsLoading || isUsersLoading,
        isParent: false,
        organization: null,
        viewProducts: [],
        viewDeals: [],
        viewContacts: [],
        viewTeam: [],
        childOrgs: [],
        relatedParentCompanies: [],
      }
    }

    const baseOrg = organizations.find(o => o?.PK === PK) ?? null
    const isParent = !!baseOrg && (PARENT_TYPES?.has(baseOrg?.type) ?? false)

    // ✅ Non-parent org: resolve contact IDs directly from the org itself
    if (!isParent) {
      const viewProducts = (products ?? []).filter(p => p?.PK === PK)
      const viewDeals = (deals ?? []).filter(d => d?.PK === PK)

      const contactIds = baseOrg ? collectOrgContactIds(baseOrg) : []
      const viewContacts = (contacts ?? []).filter(c => contactIds.includes(c.contacts_uuid))

      // Build lookup for deals by deal_uuid for checking active status
      const dealByUuid = new Map((deals ?? []).map(d => [d.deal_uuid, d]))

      // Build lookup for products by org PK
      const productsByOrgPK = new Map<string, any[]>()
      for (const p of products ?? []) {
        const pk = p?.PK
        if (!pk) continue
        const arr = productsByOrgPK.get(pk) ?? []
        arr.push(p)
        productsByOrgPK.set(pk, arr)
      }

      // Resolve parent organizations from contractedBy and affiliated IDs
      const contractedByIds = toArray(baseOrg?.contractedBy).map(normalizeId).filter(Boolean)
      const affiliatedIds = toArray(baseOrg?.affiliated).map(normalizeId).filter(Boolean)
      const parentCompanyIds = Array.from(new Set([...contractedByIds, ...affiliatedIds]))

      const relatedParentCompanies = (organizations ?? [])
        .filter(org => parentCompanyIds.includes(org?.org_uuid))
        ?.map(parentOrg => {
          // Parent companies typically don't have contractedBy, but resolve if present
          const parentContractedByIds = toArray(parentOrg?.contractedBy).map(normalizeId).filter(Boolean)
          const contractedByOrg = parentContractedByIds.length > 0
            ? organizations?.find(org => parentContractedByIds.includes(org?.org_uuid)) ?? null
            : null

          // Find all child organizations for this parent (via contractedBy or affiliated)
          const parentId = parentOrg?.org_uuid
          const childOrgsForParent = (organizations ?? []).filter(childOrg => {
            if (PARENT_TYPES?.has(childOrg?.type)) return false // skip other parents
            const childContractedBy = toArray(childOrg?.contractedBy).map(normalizeId)
            const childAffiliated = toArray(childOrg?.affiliated).map(normalizeId)
            return childContractedBy.includes(parentId) || childAffiliated.includes(parentId)
          })

          // Calculate rolled-up totals from all child orgs' products
          let price = 0
          let grossProfit = 0
          let mediaSpend = 0
          let netTotal = 0

          for (const childOrg of childOrgsForParent) {
            const childPK = childOrg?.PK || `ORG_${childOrg?.org_uuid}`
            const childProducts = productsByOrgPK.get(childPK) ?? []

            // Filter to active products only
            const activeProducts = childProducts.filter(p => {
              const relatedDeal = dealByUuid.get(p?.deal_uuid)
              if (!relatedDeal) return false
              return !['Cancelled', 'Replaced', 'Ended'].includes(relatedDeal?.dealStatus)
            })

            for (const p of activeProducts) {
              price += n(p?.price)
              grossProfit += n(p?.grossProfit)
              mediaSpend += n(p?.mediaSpend)
              netTotal += n(p?.netTotal)
            }
          }

          return {
            ...parentOrg,
            contractedBy: contractedByOrg,
            price: round2(price),
            grossProfit: round2(grossProfit),
            mediaSpend: round2(mediaSpend),
            netTotal: round2(netTotal),
            childCount: childOrgsForParent.length,
          }
        })

      return {
        isItemsLoading: isItemsLoading || isContactsLoading,
        isParent: false,
        organization: baseOrg,
        childOrgs: [],
        relatedParentCompanies,
        viewProducts,
        viewDeals,
        viewContacts,
      }
    }

    // parent rollup object comes from bootstrap-derived parents
    const parentOrg = (parents ?? []).find(p => p?.PK === PK) ?? baseOrg

    const viewProducts = Array.isArray(parentOrg?.products) ? parentOrg.products : []
    const existingCustomProducts = (custom_products ?? [])
      .filter(p => p?.PK === PK)
      .filter(p => String(p?.SK ?? '').startsWith('PRODUCT_CUSTOM_'))
      .filter(p => !p?.isArchived)
    const archived_products = (custom_products ?? []).filter(p => p?.PK === PK).filter(p => p?.isArchived)
    const viewDeals = Array.isArray(parentOrg?.deals) ? parentOrg.deals : []

    // Build lookup for deals by deal_uuid for checking active status
    const dealByUuid = new Map((deals ?? []).map(d => [d.deal_uuid, d]))

    // Build lookup for products by org PK
    const productsByOrgPK = new Map<string, any[]>()
    for (const p of products ?? []) {
      const pk = p?.PK
      if (!pk) continue
      const arr = productsByOrgPK.get(pk) ?? []
      arr.push(p)
      productsByOrgPK.set(pk, arr)
    }

    const childOrgs = Array.isArray(parentOrg?.childOrganizations)
      ? parentOrg.childOrganizations?.map(o => {
          // contractedBy is an array of org_uuid strings - resolve the first one to a full org object
          const contractedByIds = toArray(o?.contractedBy).map(normalizeId).filter(Boolean)
          const contractedByOrg = contractedByIds.length > 0
            ? organizations?.find(org => contractedByIds.includes(org?.org_uuid)) ?? null
            : null

          // Calculate financial totals from products for this child org
          const childPK = o?.PK || `ORG_${o?.org_uuid}`
          const childProducts = productsByOrgPK.get(childPK) ?? []

          // Filter to active products only (exclude Cancelled, Replaced, Ended deals)
          const activeProducts = childProducts.filter(p => {
            const relatedDeal = dealByUuid.get(p?.deal_uuid)
            if (!relatedDeal) return false
            return !['Cancelled', 'Replaced', 'Ended'].includes(relatedDeal?.dealStatus)
          })

          // Sum up financials from active products
          let price = 0
          let grossProfit = 0
          let mediaSpend = 0
          let netTotal = 0

          for (const p of activeProducts) {
            price += n(p?.price)
            grossProfit += n(p?.grossProfit)
            mediaSpend += n(p?.mediaSpend)
            netTotal += n(p?.netTotal)
          }

          return {
            ...o,
            contractedBy: contractedByOrg,
            price: round2(price),
            grossProfit: round2(grossProfit),
            mediaSpend: round2(mediaSpend),
            netTotal: round2(netTotal),
          }
        })
      : []

    const contactIds = Array.from(
      new Set(
        (childOrgs ?? [])
          .flatMap(org => collectOrgContactIds(org))
          .map(normalizeId)
          .filter(Boolean)
      )
    )
    const teamIds = Array.from(
      new Set(
        (childOrgs ?? [])
          .flatMap(org => collectOrgTeamIds(org))
          .map(normalizeId)
          .filter(Boolean)
      )
    )
    const viewContacts = (contacts ?? []).filter(c => contactIds.includes(c.contacts_uuid))
    const viewTeam = (users ?? []).filter(c => teamIds.includes(c.user_uuid))

    return {
      isItemsLoading: isItemsLoading || isContactsLoading,
      isParent: true,
      organization: parentOrg,
      childOrgs,
      relatedParentCompanies: [],
      viewProducts,
      existingCustomProducts,
      archived_products,
      viewDeals,
      viewContacts,
      viewTeam,
    }
  }, [PK, organizations, parents, deals, products, custom_products, contacts, users, isItemsLoading, isContactsLoading])
}
